import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { Trash2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { FormField, Input } from '@/components/ui/FormField';
import { Alert } from '@/components/ui/Alert';
import { SelectField } from './SelectField';
import {
    useAddClientAuth,
    useEditClientAuth,
    useRemoveClientAuth,
    type ClientAuth,
} from '@/api/clients';
import {
    useActivePayers,
    useActiveServiceTypes,
} from '@/api/referenceData';

/*
 * Shared dialog for adding or editing an authorization entry. When
 * `auth` is non-null the form opens pre-populated in edit mode and
 * exposes a Delete button. When null it opens blank for a new entry.
 *
 * Payer / auth number / CPT code / hours are PHI-adjacent billing
 * identifiers, so every save / delete writes an audit row server-side.
 * The server is the authoritative validator; this dialog surfaces any
 * response error through an Alert.
 */

interface AuthDialogProps {
    clientId: string;
    auth: ClientAuth | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

type FormState = {
    payer:  string;
    number: string;
    code:   string;
    hours:  string;
    used:   string;
    start:  string;
    end:    string;
};

const EMPTY_FORM: FormState = {
    payer: '', number: '', code: '',
    hours: '', used:  '',
    start: '', end:   '',
};

export function AuthDialog({ clientId, auth, open, onOpenChange }: AuthDialogProps) {
    const addMut    = useAddClientAuth(clientId);
    const editMut   = useEditClientAuth(clientId);
    const deleteMut = useRemoveClientAuth(clientId);
    const payersQuery = useActivePayers();
    const serviceTypesQuery = useActiveServiceTypes();
    const payers = payersQuery.data ?? [];
    const serviceTypes = serviceTypesQuery.data ?? [];

    const isEdit  = auth !== null;
    const pending = addMut.isPending || editMut.isPending || deleteMut.isPending;

    const [form, setForm] = useState<FormState>(() => initialForm(auth));
    const [fieldError, setFieldError] = useState<string | null>(null);
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    // Preserve pre-fills that don't match the current catalog so edits of
    // older entries don't silently drop the stored payer / code.
    const payerInList = payers.some((p) => p.name === form.payer);
    const showUnlistedPayer = !!form.payer && !payerInList;
    const codeInList = serviceTypes.some((s) => s.code === form.code);
    const showUnlistedCode = !!form.code && !codeInList;

    useEffect(() => {
        if (open) {
            setForm(initialForm(auth));
            setFieldError(null);
            setConfirmingDelete(false);
            addMut.reset();
            editMut.reset();
            deleteMut.reset();
        }
    }, [open, auth]); // eslint-disable-line react-hooks/exhaustive-deps

    function update<K extends keyof FormState>(key: K, value: FormState[K]) {
        setForm((f) => ({ ...f, [key]: value }));
    }

    function parseNum(v: string): number | undefined {
        if (v === '' || v == null) return undefined;
        const n = Number(v);
        return Number.isFinite(n) ? n : undefined;
    }

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        const hours = parseNum(form.hours);
        const used  = parseNum(form.used);

        if (!isEdit && !form.number.trim() && !hours) {
            setFieldError('Enter an authorization number or hours (or both).');
            return;
        }
        if (form.hours && (hours == null || hours < 0)) {
            setFieldError('Hours must be a non-negative number.');
            return;
        }
        if (form.used && (used == null || used < 0)) {
            setFieldError('Used must be a non-negative number.');
            return;
        }
        setFieldError(null);

        const payload = {
            payer:  form.payer.trim(),
            number: form.number.trim(),
            code:   form.code.trim(),
            hours:  hours ?? 0,
            used:   used  ?? 0,
            start:  form.start,
            end:    form.end,
        };

        try {
            if (isEdit && auth) {
                await editMut.mutateAsync({ entryId: auth.id, payload });
            } else {
                await addMut.mutateAsync(payload);
            }
            onOpenChange(false);
        } catch {
            /* surfaced below */
        }
    }

    async function onDelete() {
        if (!auth) return;
        if (!confirmingDelete) {
            setConfirmingDelete(true);
            setTimeout(() => setConfirmingDelete(false), 5000);
            return;
        }
        try {
            await deleteMut.mutateAsync(auth.id);
            onOpenChange(false);
        } catch {
            /* surfaced below */
        }
    }

    const serverError = (() => {
        const e = addMut.error || editMut.error || deleteMut.error;
        if (!e) return null;
        if (e instanceof AxiosError) return e.response?.data?.error ?? 'Failed to save authorization';
        return 'Unexpected error';
    })();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{isEdit ? 'Edit Authorization' : 'Add Authorization'}</DialogTitle>
                    <DialogDescription>
                        Authorizations are audited on save. Enter an auth number or hours at minimum.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={onSubmit} noValidate>
                    {(serverError || fieldError) && (
                        <Alert variant="danger" className="mb-4">
                            {fieldError ?? serverError}
                        </Alert>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                        <FormField label="Payer">
                            {(id) => (
                                <SelectField
                                    id={id}
                                    value={form.payer}
                                    onChange={(e) => update('payer', e.target.value)}
                                    disabled={payersQuery.isLoading}
                                >
                                    <option value="">
                                        {payersQuery.isLoading ? 'Loading…' : 'Select a payer…'}
                                    </option>
                                    {showUnlistedPayer && (
                                        <option value={form.payer}>
                                            {form.payer} (unlisted)
                                        </option>
                                    )}
                                    {payers.map((p) => (
                                        <option key={p.id} value={p.name}>{p.name}</option>
                                    ))}
                                </SelectField>
                            )}
                        </FormField>
                        <FormField label="Authorization #">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.number}
                                    onChange={(e) => update('number', e.target.value)}
                                />
                            )}
                        </FormField>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4">
                        <FormField label="Service code">
                            {(id) => (
                                <SelectField
                                    id={id}
                                    value={form.code}
                                    onChange={(e) => update('code', e.target.value)}
                                    disabled={serviceTypesQuery.isLoading}
                                >
                                    <option value="">
                                        {serviceTypesQuery.isLoading ? 'Loading…' : 'Select a code…'}
                                    </option>
                                    {showUnlistedCode && (
                                        <option value={form.code}>
                                            {form.code} (unlisted)
                                        </option>
                                    )}
                                    {serviceTypes.map((s) => (
                                        <option key={s.id} value={s.code}>
                                            {s.code}{s.name ? ` — ${s.name}` : ''}
                                        </option>
                                    ))}
                                </SelectField>
                            )}
                        </FormField>
                        <FormField label="Hours">
                            {(id) => (
                                <Input
                                    id={id}
                                    type="number"
                                    min="0"
                                    step="0.25"
                                    value={form.hours}
                                    onChange={(e) => update('hours', e.target.value)}
                                />
                            )}
                        </FormField>
                        <FormField label="Used">
                            {(id) => (
                                <Input
                                    id={id}
                                    type="number"
                                    min="0"
                                    step="0.25"
                                    value={form.used}
                                    onChange={(e) => update('used', e.target.value)}
                                />
                            )}
                        </FormField>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                        <FormField label="Start date">
                            {(id) => (
                                <Input
                                    id={id}
                                    type="date"
                                    min="1900-01-01"
                                    max="9999-12-31"
                                    value={form.start}
                                    onChange={(e) => update('start', e.target.value)}
                                />
                            )}
                        </FormField>
                        <FormField label="End date">
                            {(id) => (
                                <Input
                                    id={id}
                                    type="date"
                                    min={form.start || '1900-01-01'}
                                    max="9999-12-31"
                                    value={form.end}
                                    onChange={(e) => update('end', e.target.value)}
                                />
                            )}
                        </FormField>
                    </div>

                    <DialogFooter className="justify-between">
                        <div>
                            {isEdit && (
                                <Button
                                    type="button"
                                    variant={confirmingDelete ? 'danger' : 'ghost'}
                                    size="sm"
                                    onClick={onDelete}
                                    disabled={pending}
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    {confirmingDelete ? 'Click again to confirm' : 'Delete'}
                                </Button>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                disabled={pending}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" variant="primary" disabled={pending}>
                                {pending ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Authorization'}
                            </Button>
                        </div>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function initialForm(auth: ClientAuth | null): FormState {
    if (!auth) return { ...EMPTY_FORM };
    return {
        payer:  auth.payer  ?? '',
        number: auth.number ?? '',
        code:   auth.code   ?? '',
        hours:  auth.hours ? String(auth.hours) : '',
        used:   auth.used  ? String(auth.used)  : '',
        start:  auth.start  ?? '',
        end:    auth.end    ?? '',
    };
}
